import { Injectable, computed, signal } from "@angular/core";
import { MainPage } from "@app/shared/types/pages";
import { StatusMessage } from "@core/domain/status-message";

@Injectable({
  providedIn: "root",
})
export class StatusMessageService {
  planStatusMessages = signal<StatusMessage[] | undefined>(undefined);
  catalogStatusMessages = signal<StatusMessage[] | undefined>(undefined);
  curriculumStatusMessages = signal<StatusMessage[] | undefined>(undefined);
  mainPage: MainPage = MainPage.PLAN;

  readonly hasPlanStatusMessages = computed<boolean>(() => {
    return !!this.planStatusMessages()?.length;
  });
  readonly hasCatalogStatusMessages = computed<boolean>(() => {
    return !!this.catalogStatusMessages()?.length;
  });
  readonly hasCurriculumStatusMessages = computed<boolean>(() => {
    return !!this.curriculumStatusMessages()?.length;
  });

  setStatusMessages(statusMessages?: StatusMessage[], forPage?: MainPage): void {
    // page defaults to PLAN page
    const page = forPage ?? this.mainPage;
    switch (page) {
      case MainPage.PLAN:
        this.planStatusMessages.set(statusMessages);
        break;
      case MainPage.CATALOG:
        this.catalogStatusMessages.set(statusMessages);
        break;
      case MainPage.CURRICULUM:
        this.curriculumStatusMessages.set(statusMessages);
        break;
    }
    if (statusMessages?.length) {
      this.scrollToMessages();
    }
  }

  private scrollToMessages(): void {
    const startTime = performance.now();
    const tryScroll = () => {
      // wait for pa-messages-alert to be present and then scroll to it
      const messagesElement = document.querySelector("pa-messages-alert");
      if (messagesElement) {
        messagesElement.scrollIntoView({ behavior: "smooth" });
      } else if (performance.now() - startTime < 500) {
        requestAnimationFrame(tryScroll);
      }
      // else: stop trying after 500ms, to prevent an infinite loop
    };
    tryScroll();
  }

  clearStatusMessages(): void {
    switch (this.mainPage) {
      case MainPage.PLAN:
        this.planStatusMessages.set(undefined);
        break;
      case MainPage.CATALOG:
        this.catalogStatusMessages.set(undefined);
        break;
      case MainPage.CURRICULUM:
        this.curriculumStatusMessages.set(undefined);
        break;
    }
  }

  clearAllStatusMessages(): void {
    this.planStatusMessages.set(undefined);
    this.catalogStatusMessages.set(undefined);
    this.curriculumStatusMessages.set(undefined);
  }

  setMainPage(mainPage: MainPage): void {
    this.mainPage = mainPage;
  }
}
